import { useNavigate, useParams } from 'react-router-dom'
import { faFull, faPlain, longJalali } from 'utilities/helper/format'

/**
 * وام‌های عضو — one row per loan, tappable through to that loan's own page.
 *
 * The old «سابقه مالی عضو» had a loans tab and the first cut of this page
 * replaced it with a summary. «۶ وام، مجموع ۴۲٬۱۴۰٬۰۰۰» does not say which
 * loan is still running or which one went bad, and the follow-up a manager
 * is about to have is always about one specific loan.
 *
 * The progress band is filled from the RIGHT, matching the old loan rows and
 * every other bar in the app.
 */
export default function LoanList({ loans }: { loans: IMemberLoanRow[] }) {
    const navigate = useNavigate()
    const { moneyPool, poolship } = useParams()

    if (!loans.length) return null

    // جاری vs بسته‌شده: a settled loan is history, a running one is a live
    // obligation. Mixed into one list the manager has to do that sort in
    // their head every time they open the page.
    const open = loans.filter((loan) => !loan.is_settled)
    const settled = loans.filter((loan) => loan.is_settled)

    const group = (rows: IMemberLoanRow[], title: string) => {
        if (!rows.length) return null
        return (
            <>
                <p className=" mt-3 text-xs font-bold text-heading">
                    {title} ({faPlain.format(rows.length)})
                </p>
                <div className=" mt-2 flex flex-col gap-2">{rows.map(row)}</div>
            </>
        )
    }

    function row(loan: IMemberLoanRow) {
                const progress =
                    loan.installments_total > 0
                        ? Math.min(
                              loan.installments_paid / loan.installments_total,
                              1
                          ) * 100
                        : 0
                const chip = loan.is_settled
                    ? { text: 'تسویه‌شده', className: ' bg-success-2 text-success-on' }
                    : loan.overdue_amount > 0
                    ? {
                          text: `${faPlain.format(loan.max_days_overdue)} روز معوق`,
                          className: ' bg-danger-soft text-danger-on',
                      }
                    : { text: 'در جریان', className: ' bg-brand-soft text-brand-deep' }

                return (
                    <div
                        key={loan.loan_id}
                        role="button"
                        tabIndex={0}
                        onClick={() =>
                            navigate(
                                `/fund/financial-history/funds-detail/${moneyPool}/${poolship}/${loan.loan_id}`
                            )
                        }
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                navigate(
                                    `/fund/financial-history/funds-detail/${moneyPool}/${poolship}/${loan.loan_id}`
                                )
                            }
                        }}
                        className=" cursor-pointer rounded-xl border border-divider p-3 active:scale-[0.99]"
                    >
                        <div className=" flex items-center justify-between gap-2">
                            <p className=" text-sm font-bold text-heading">
                                {faFull.format(loan.amount)}{' '}
                                <span className=" text-[11px] font-normal text-text-secondary">
                                    تومان
                                </span>
                            </p>
                            <span
                                className={` shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${chip.className}`}
                            >
                                {chip.text}
                            </span>
                        </div>

                        {!!loan.pay_date && (
                            <p className=" mt-1 text-[11px] text-text-secondary">
                                {longJalali(loan.pay_date)}
                            </p>
                        )}

                        <div className=" relative mt-2 h-1.5 w-full overflow-hidden rounded-full bg-canvas">
                            <span
                                className=" absolute inset-y-0 right-0 rounded-full"
                                style={{
                                    width: `${progress}%`,
                                    background: loan.is_settled
                                        ? '#13BF86'
                                        : loan.overdue_amount > 0
                                        ? '#F24C3D'
                                        : '#74B743',
                                }}
                            />
                        </div>

                        <div className=" mt-1.5 flex items-center justify-between gap-2">
                            <p className=" text-[11px] text-text-secondary">
                                {faPlain.format(loan.installments_paid)} از{' '}
                                {faPlain.format(loan.installments_total)} قسط
                            </p>
                            {loan.remaining > 0 && (
                                <p className=" text-[11px] text-text-secondary">
                                    مانده {faFull.format(loan.remaining)} تومان
                                </p>
                            )}
                        </div>
                    </div>
                )
    }

    return (
        <div className=" flex flex-col">
            {group(open, 'وام‌های جاری')}
            {group(settled, 'وام‌های بسته‌شده')}
        </div>
    )
}
